// Kartlag «SKANDIA-styrker» — svenske (Hemvärnet) og danske (Hjemmeværnet)
// enheter plassert på kartet via milsymbol/APP-6 (samme render-vei som norske
// enheter). Data: @skade/contracts skandiaPlacementsValid() (orbat-seed + verifiserte
// koordinater). Togglebart, AV som standard. Klikk → detaljboble.
import type { Map as MlMap, MapGeoJSONFeature } from 'maplibre-gl';
import { featureCollection, skandiaPlacementsValid, type GeoFeature } from '@skade/contracts';
import { ensureSymbol } from '../symbols.js';

const LAYER = 'skandia';

export class SkandiaLag {
  private visible = false;
  private ready = false;

  constructor(private map: MlMap) {
    if (map.loaded()) this.init();
    else map.on('load', () => this.init());
  }

  private toFeatures(): GeoFeature[] {
    return skandiaPlacementsValid().map((p) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
      properties: { id: p.id, name: p.name, sidc: p.sidc, nation: p.nation, echelon: p.echelon, hq: p.hq },
    }));
  }

  private init() {
    if (this.ready || this.map.getSource(LAYER)) return;
    const feats = this.toFeatures();
    // Registrer et milsymbol-bilde per unik SIDC.
    for (const f of feats) {
      const sidc = (f.properties as { sidc?: string }).sidc;
      if (sidc) ensureSymbol(this.map, sidc);
    }
    this.map.addSource(LAYER, { type: 'geojson', data: featureCollection(feats) as never });
    this.map.addLayer({
      id: LAYER, type: 'symbol', source: LAYER,
      layout: {
        visibility: 'none',
        'icon-image': ['get', 'sidc'],
        'icon-size': ['interpolate', ['linear'], ['zoom'], 4, 0.4, 7, 0.55, 11, 0.75],
        'icon-allow-overlap': true,
        'text-field': ['get', 'name'], 'text-font': ['Open Sans Regular'],
        'text-size': 10, 'text-offset': [0, 1.5], 'text-anchor': 'top',
        'text-optional': true, 'text-max-width': 12,
      },
      paint: { 'text-color': '#c8d8c8', 'text-halo-color': '#0a0c0a', 'text-halo-width': 1.4 },
      minzoom: 4,
    });
    this.ready = true;
  }

  toggle(): boolean {
    this.init();
    this.visible = !this.visible;
    this.map.setLayoutProperty(LAYER, 'visibility', this.visible ? 'visible' : 'none');
    return this.visible;
  }
  isOn(): boolean { return this.visible; }

  hitAt(point: { x: number; y: number }): MapGeoJSONFeature | null {
    if (!this.map.getLayer(LAYER)) return null;
    const hits = this.map.queryRenderedFeatures([point.x, point.y] as never, { layers: [LAYER] });
    return hits[0] ?? null;
  }
}
